
import React from 'react';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;     
  isFreeAccessBlocked: boolean;
  setIsFreeAccessBlocked: (blocked: boolean) => void;
  isDeviceAllowed: boolean;
  onAllowDevice: () => void;
  onRemoveDevice: () => void;
  onNavigateToAreaList: () => void;
  onNavigateToEmployeeList: () => void;
}

const CloseIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>;
const MoonIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" /></svg>;
const SunIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.121-3.536a1 1 0 011.414 0l.707.707a1 1 0 01-1.414 1.414l-.707-.707a1 1 0 010-1.414zM10 16a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM4.95 14.536L4.243 15.243a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM2.757 9.243a1 1 0 010 1.414l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 0zM15.05 4.95l.707-.707a1 1 0 00-1.414-1.414l-.707-.707a1 1 0 101.414 1.414zM5.464 4.536a1 1 0 01-1.414 0l-.707-.707a1 1 0 011.414-1.414l.707.707a1 1 0 010 1.414z" clipRule="evenodd" /></svg>;
const ChevronRightIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" /></svg>;
const LockOpenIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path d="M10 2a5 5 0 00-5 5v2a2 2 0 00-2 2v5a2 2 0 002 2h10a2 2 0 002-2v-5a2 2 0 00-2-2V7a5 5 0 00-5-5zm0 2.5a2.5 2.5 0 012.5 2.5V9h-5V7a2.5 2.5 0 012.5-2.5z" /></svg>;
const LockClosedIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H3a2 2 0 01-2-2v-5a2 2 0 012-2h2zm4-2a3 3 0 00-3 3v2h6V7a3 3 0 00-3-3z" clipRule="evenodd" /></svg>;

const DarkModeToggle: React.FC<{ isDarkMode: boolean; setIsDarkMode: (isDark: boolean) => void }> = ({ isDarkMode, setIsDarkMode }) => {
  return (
    <div className="flex items-center justify-between w-full p-2 rounded-lg">
      <label htmlFor="dark-mode-toggle" className="text-on-surface dark:text-dark-on-surface cursor-pointer font-medium">
        Modo Oscuro
      </label>
      <button
        id="dark-mode-toggle"
        role="switch"
        aria-checked={isDarkMode}
        onClick={() => setIsDarkMode(!isDarkMode)}
        className={`relative inline-flex items-center h-8 w-14 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-dark-surface-container-high ${isDarkMode ? 'bg-primary dark:bg-dark-primary' : 'bg-surface-variant dark:bg-dark-surface-variant'}`}
      >
        <span
          className={`absolute flex items-center justify-center h-6 w-6 transform bg-white dark:bg-dark-on-primary rounded-full transition-transform duration-300 ${isDarkMode ? 'translate-x-7 bg-on-primary dark:bg-dark-on-primary' : 'translate-x-1 bg-outline dark:bg-dark-outline'}`}
        >
          {isDarkMode ? <SunIcon /> : <MoonIcon />}
        </span>
      </button>
    </div>
  );
};

const SettingsToggle: React.FC<{
    label: string;
    id: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    OnIcon: React.FC;
    OffIcon: React.FC;
  }> = ({ label, id, checked, onChange, OnIcon, OffIcon }) => {
    return (
      <div className="flex items-center justify-between w-full p-2 rounded-lg">
        <label htmlFor={id} className="text-on-surface dark:text-dark-on-surface cursor-pointer font-medium">
          {label}
        </label>
        <button
          id={id}
          role="switch"
          aria-checked={checked}
          onClick={() => onChange(!checked)}
          className={`relative inline-flex items-center h-8 w-14 rounded-full transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary dark:focus:ring-offset-dark-surface-container-high ${checked ? 'bg-primary dark:bg-dark-primary' : 'bg-surface-variant dark:bg-dark-surface-variant'}`}
        >
          <span
            className={`absolute flex items-center justify-center h-6 w-6 transform bg-white dark:bg-dark-surface-container rounded-full transition-transform duration-300 ${checked ? 'translate-x-7 ' : 'translate-x-1'}`}
            >
            <span className={`transition-opacity duration-200 ${checked ? 'opacity-100 text-on-primary dark:text-dark-on-primary' : 'opacity-0'}`}><OnIcon /></span>
            <span className={`absolute transition-opacity duration-200 ${!checked ? 'opacity-100 text-on-surface-variant dark:text-dark-on-surface-variant' : 'opacity-0'}`}><OffIcon /></span>
          </span>
        </button>
      </div>
    );
  };

const SettingsModal: React.FC<SettingsModalProps> = ({ isOpen, onClose, onLogout, isFreeAccessBlocked, setIsFreeAccessBlocked, isDeviceAllowed, onAllowDevice, onRemoveDevice, onNavigateToAreaList, onNavigateToEmployeeList }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-scrim/30 dark:bg-scrim/50 z-40 flex items-center justify-center p-4 animate-fade-in" onClick={onClose} aria-modal="true" role="dialog">
      <div className="relative bg-surface-container-high dark:bg-dark-surface-container-high rounded-3xl shadow-2xl w-full max-w-sm m-4 transform animate-scale-in" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b border-outline-variant dark:border-dark-outline-variant">
            <h2 className="text-xl font-medium text-on-surface dark:text-dark-on-surface">Configuración</h2>
            <button onClick={onClose} className="text-on-surface-variant dark:text-dark-on-surface-variant hover:bg-error/10 dark:hover:bg-dark-error/10 rounded-full p-1 transition-colors" aria-label="Cerrar modal">
                <CloseIcon />
            </button>
        </div>

        <div className="p-4 sm:p-6 space-y-2">                      
            <h3 className="px-2 pt-1 text-sm font-semibold text-primary dark:text-dark-primary" style={{textAlign:"left"}}>Acceso y Seguridad</h3>            
            <div>
                <SettingsToggle label="Bloquear Libre Acceso" id="block-access-toggle" checked={isFreeAccessBlocked} onChange={setIsFreeAccessBlocked} OnIcon={LockClosedIcon} OffIcon={LockOpenIcon} />
                <p className="px-2 text-xs text-on-surface-variant dark:text-dark-on-surface-variant" style={{textAlign:"left"}}>Si se activa, los empleados no podrán registrar su propia asistencia sin supervisión.</p>
            </div>
            
            <div className="p-2 space-y-2">
                <p className="text-on-surface dark:text-dark-on-surface font-medium" style={{textAlign:"left"}}>Gestión de Dispositivo</p>
                <p className="text-sm text-on-surface-variant dark:text-dark-on-surface-variant" style={{textAlign:"left"}}>
                    {isDeviceAllowed ? 'Este dispositivo está permitido para registrar asistencia.' : 'Este dispositivo no está permitido.'}
                </p>
                {isDeviceAllowed ? (
                     <button onClick={onRemoveDevice} className="w-full text-left p-2 rounded-lg text-error dark:text-dark-error font-medium hover:bg-error/10 dark:hover:bg-dark-error/10 transition-colors">
                        Olvidar este dispositivo
                    </button>
                ) : (
                    <button onClick={onAllowDevice} className="w-full text-left p-2 rounded-lg text-primary dark:text-dark-primary font-medium hover:bg-primary/10 dark:hover:bg-dark-primary/10 transition-colors">
                        Permitir este dispositivo
                    </button>
                )}
            </div>
            
            <div className="border-t border-outline-variant dark:border-dark-outline-variant my-2"></div>
            <h3 className="px-2 pt-1 text-sm font-semibold text-primary dark:text-dark-primary" style={{textAlign:"left"}}>Administración</h3>

            <button onClick={onNavigateToEmployeeList} className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-surface-container-highest dark:hover:bg-dark-surface-container-highest transition-colors">
                <span className="text-on-surface dark:text-dark-on-surface font-medium">Gestionar Empleados</span>
                <ChevronRightIcon />
            </button>

            <button onClick={onNavigateToAreaList} className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-surface-container-highest dark:hover:bg-dark-surface-container-highest transition-colors">
                <span className="text-on-surface dark:text-dark-on-surface font-medium">Gestionar Áreas</span>
                <ChevronRightIcon />
            </button>

            <div className="border-t border-outline-variant dark:border-dark-outline-variant my-2"></div>
                        
            <button
                onClick={onLogout}
                className="w-full text-left p-2 rounded-lg text-error dark:text-dark-error font-medium hover:bg-error/10 dark:hover:bg-dark-error/10 transition-colors"
            >
                Cerrar Sesión
            </button>
        </div> 
        
      </div>
      <style>{`
        @keyframes fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
        @keyframes scale-in { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-scale-in { animation: scale-in 0.3s ease-out forwards; }
      `}</style>
    </div>
  );
};

export default SettingsModal;
